// Dialer country gate: the picker lists EVERY country, and this decides what
// the dialer says before it lets a call to `iso` go out. Pure and advisory —
// the server gates (TwiML app, Telnyx bridge, /api/calls) stay the authority.
//
// Precedence (owner decision, 2026-10):
//   1. a free test destination (verified number, AI demo line) → allowed
//   2. a country the account isn't entitled to → KYC notice (even on a trial:
//      identity verification is the bigger blocker); if the account is ALREADY
//      verified, KYC can't help → "not available, contact support"
//   3. an entitled country while trialing → "paid accounts" notice. Domestic
//      (US/CA/GB) destinations keep the #1537 trial message instead — the
//      "other countries" wording only makes sense abroad.
//   4. otherwise allowed.
import { normalizeCountryCode, type Capabilities } from '@shared/capabilities';
import { getPhoneCountries, type PhoneCountry } from './phoneCountries';
import { TRIAL_OUTBOUND_MESSAGE } from './freeTestCalls';

export const KYC_COUNTRY_MESSAGE = 'Complete ID verification (KYC) to enable outgoing calls to all countries';
export const TRIAL_COUNTRY_MESSAGE = 'Calling other countries is available on paid accounts. Start your plan to call all allowed countries.';
export const unavailableCountryMessage = (country: string) => `Calls to ${country} aren't available for your account — contact support`;

const DOMESTIC = new Set(['US', 'CA', 'GB']);

export type CountryGate =
  | { kind: 'allowed' }
  | { kind: 'kyc'; message: string }
  | { kind: 'unavailable'; message: string }
  | { kind: 'trial'; message: string; domestic: boolean };

/**
 * Is the account ENTITLED to call this ISO country — its plan regions plus the
 * admin's per-country exceptions — ignoring the trial/outbound switch, which
 * this gate reports separately?
 */
export function countryEntitled(caps: Pick<Capabilities, 'voice'> | null | undefined, iso: string | null | undefined): boolean {
  const code = String(iso ?? '').trim().toUpperCase();
  if (!caps?.voice || !code) return false;
  if (caps.voice.allowedIsoCountries?.includes(code)) return true;
  const region = normalizeCountryCode(code);
  return !!region && (caps.voice.allowedCountries || []).includes(region);
}

export function decideCountryGate(p: {
  iso: string | null | undefined;
  countryName?: string;
  isTestDestination: boolean;
  entitled: boolean;
  trialing: boolean;
  kycVerified: boolean;
}): CountryGate {
  if (p.isTestDestination) return { kind: 'allowed' };
  if (!p.entitled) {
    return p.kycVerified
      ? { kind: 'unavailable', message: unavailableCountryMessage(p.countryName || String(p.iso || 'this country')) }
      : { kind: 'kyc', message: KYC_COUNTRY_MESSAGE };
  }
  if (p.trialing) {
    const domestic = DOMESTIC.has(String(p.iso || '').toUpperCase());
    return { kind: 'trial', domestic, message: domestic ? TRIAL_OUTBOUND_MESSAGE : TRIAL_COUNTRY_MESSAGE };
  }
  return { kind: 'allowed' };
}

/** Every country (libphonenumber metadata), entitled ones first, each group by name. */
export function pickerCountries(caps: Pick<Capabilities, 'voice'> | null | undefined, all: PhoneCountry[] = getPhoneCountries()) {
  const rows = all.map((c) => ({ ...c, entitled: countryEntitled(caps, c.iso) }));
  return [...rows.filter((c) => c.entitled), ...rows.filter((c) => !c.entitled)];
}

/** Start the account's identity verification (hosted Stripe/Didit page). Owner/admin only server-side. */
export async function startIdentityVerification(post: (url: string, body: unknown) => Promise<any>): Promise<{ url?: string; error?: string }> {
  try {
    const r = await post('/api/account/kyc/start', { kind: 'person' });
    return r?.url ? { url: String(r.url) } : { error: 'Verification is not available right now. Please contact support.' };
  } catch (e: any) {
    const msg = String(e?.message || '');
    return { error: /403/.test(msg) ? 'Ask your account owner or an admin to complete ID verification.' : (msg.replace(/^\d{3}:\s*/, '') || 'Could not start verification.') };
  }
}
